import { memo, useMemo } from 'react';

import Button from '@atlaskit/button/new';
import DropdownMenu, {
  DropdownItem,
  DropdownItemGroup,
} from '@atlaskit/dropdown-menu';
import ChevronDownIcon from '@atlaskit/icon/core/chevron-down';
import Tooltip from '@atlaskit/tooltip';

import { Action, DisabledState } from './Action';
import SingleActionButton from './SingleActionButton';
import { ButtonOverlay, MultiActionButtonWrapper } from './styled';

export interface Props {
  title: string;
  isLoading?: boolean;
  disabled?: DisabledState;
  actions: Action[];
}

const MultiActionButton = ({
  title,
  isLoading = false,
  disabled = DisabledState.empty,
  actions,
}: Props) => {
  const availableActions = useMemo(
    () => actions.filter(a => a.isVisible),
    [actions]
  );
  const singleAction = useMemo(() => {
    if (availableActions.length === 1) {
      return {
        ...availableActions[0],
        disabled: disabled.or(
          availableActions[0].disabled || DisabledState.empty
        ),
      };
    } else {
      return null;
    }
  }, [disabled, availableActions]);

  if (!availableActions.length) {
    return null;
  }

  if (singleAction) {
    return <SingleActionButton action={singleAction} isLoading={isLoading} />;
  }

  return (
    <MultiActionButtonWrapper>
      <Tooltip content={!isLoading && disabled?.reason} position="left">
        {!isLoading && disabled?.value && <ButtonOverlay />}
        <DropdownMenu
          trigger={({ triggerRef, ...props }) => (
            <Button
              {...props}
              isDisabled={disabled?.value}
              spacing="compact"
              iconAfter={iconProps => (
                <ChevronDownIcon
                  {...iconProps}
                  size="small"
                  spacing="compact"
                />
              )}
              ref={triggerRef}
              isLoading={isLoading}
              testId="dropdown-menu"
            >
              {title}
            </Button>
          )}
          placement="bottom-end"
        >
          <DropdownItemGroup>
            {actions.map(
              action =>
                action.isVisible && (
                  <Tooltip
                    content={action.disabled?.reason}
                    position="left"
                    key={action.key}
                  >
                    <DropdownItem
                      onClick={action.onClick}
                      isDisabled={action.disabled?.value}
                      testId={`dropdown-item-${action.key}`}
                    >
                      {action.name}
                    </DropdownItem>
                  </Tooltip>
                )
            )}
          </DropdownItemGroup>
        </DropdownMenu>
      </Tooltip>
    </MultiActionButtonWrapper>
  );
};

// eslint-disable-next-line import/no-default-export
export default memo(MultiActionButton);
